import React, { useRef, useEffect } from "react";

import RadioField from "./radio-field";
import SelectField from "./select-field";

import CustomToast from "../../custom-toast";
import useViewSize from "../../../hooks/use-view-size";

//Pregunta de tipo matriz
//Variables: handlechange para los observadores de cambios, el número de filas y columnas de la matriz, el listado de opciones del selector (si tiene uno)
// y si la matriz está invertida (las filas y columnas se deben invertir).
const MatrixSurveyInput = ({
  dimensionIndex,
  questionIndex,
  handleChange,
  defaultAnswer,
  matrixType,
  rows,
  columns = [],
  rowColors,
  options,
  orientation,
  requiresUniqueAnswers,
}) => {
  const { width } = useViewSize();
  const isMobile = width <= 768;
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [isMobile]);  

  const handleRadioFieldChange = (event) => {
    if (!defaultAnswer || !requiresUniqueAnswers) {
      handleChange(event);
      return;
    }

    const { value } = event.target;
    const allValuesFromAnswer = defaultAnswer.map((item) => item.value);
    const answeredSameValue = allValuesFromAnswer.indexOf(value) !== -1;

    if (answeredSameValue) {
      CustomToast.error({
        messageTitle: "Atención!",
        Body: (
          <h6 className="text-justify">
            No puedes marcar la misma alternativa en dos{" "}
            {orientation === "column" ? "columnas" : "filas"} en simultáneo. Revisa tus selecciones
            si deseas modificar una de ellas.
          </h6>
        ),
        showDismissButton: true,
      });
      event.preventDefault();
      return;
    }

    handleChange(event);
  };

  // Renderiza un selector de opciones o un radio button en la celda especificada
  const renderInputByCell = (rowIndex = 0, row = "", columnIndex = 0, column = "") => {
    const cellName = `survey-question-field-matrix_${dimensionIndex}_${questionIndex}-cell-${rowIndex}_${columnIndex}`;
    const numberOfColumns = columns.length > 0 ? columns.length : 1;

    let defaultValueFromAnswer;
    switch (matrixType) {
      case "selects":
        defaultValueFromAnswer = defaultAnswer
          ? defaultAnswer[columnIndex + rowIndex * numberOfColumns]["value"]
          : "";
        return (
          <SelectField
            name={cellName}
            defaultValueFromAnswer={defaultValueFromAnswer}
            handleChange={handleChange}
            options={options}
          />
        );
      case "radios":
      default:
        defaultValueFromAnswer =
          defaultAnswer &&
          defaultAnswer[orientation === "column" ? columnIndex : rowIndex]["value"];
        return (
          <label>
            <RadioField
              name={cellName}
              value={orientation === "column" ? row : column}
              defaultValueFromAnswer={defaultValueFromAnswer}
              handleChange={handleRadioFieldChange}
            />
          </label>
        );
    }
  };

  const isSimpleNumber = (value) => /^\d{1,2}$/.test(String(value).trim());

  const getHeaderDisplay = (column, columnIndex) =>
    isSimpleNumber(column) ? column : columnIndex + 1;  

  const legendColumns = columns
    .map((column, columnIndex) => ({ column, columnIndex }))
    .filter(({ column, columnIndex }) => String(getHeaderDisplay(column, columnIndex)) !== String(column));

 return (
    <>
      {isMobile && legendColumns.length > 0 && (
        <div className="matrix-mobile-legend">
          {legendColumns.map(({ column, columnIndex }) => (
            <div key={`legend-${dimensionIndex}_${questionIndex}-${columnIndex}`}>
              <strong>{getHeaderDisplay(column, columnIndex)}</strong> — {column}
            </div>
          ))}
        </div>
      )}
      <div className="matrix-scroll-wrapper" ref={scrollContainerRef}>
        <table className="matrix">
          <thead>
            {columns.length > 0 && (
              <tr>
                <th scope="col" className="matrix-sticky-col"></th>
                {columns.map((column, columnIndex) => (
                  <td
                    key={`survey-question-field-matrix-${dimensionIndex}_${questionIndex}-header-${columnIndex}`}
                    style={!isMobile ? { width: `${width <= 1368 ? 60 / columns.length : 40 / columns.length}%` } : undefined}
                  >
                    {isMobile ? getHeaderDisplay(column, columnIndex) : column}
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={`survey-question-field-matrix-${dimensionIndex}_${questionIndex}-row-${rowIndex}`}
                style={{
                  backgroundColor: rowColors
                    ? `var(--ulagos-pastel-${rowColors[rowIndex]})`
                    : "var(--ulagos-pastel-blue)",
                }}
              >
                <th className="matrix-sticky-col">{row}</th>
                {columns.length > 0 ? (
                  columns.map((column, columnIndex) => (
                    <td
                      key={`survey-question-field-matrix-${dimensionIndex}_${questionIndex}-cell-${rowIndex}_${columnIndex}`}
                    >
                      {renderInputByCell(rowIndex, row, columnIndex, column)}
                    </td>
                  ))
                ) : (
                  <td
                    key={`survey-question-field-matrix-${dimensionIndex}_${questionIndex}-cell-${rowIndex}_0`}
                    style={{ textAlign: "center", width: "30%" }}
                  >
                    {renderInputByCell(rowIndex, row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MatrixSurveyInput;
